// ════════════════════════════════════════════════════════════════════
// LYRAEA — BookView.js   ·   the audiobook half of the now-playing sheet
// ────────────────────────────────────────────────────────────────────
//   BookControls — −15 · ◀ chapter · play · chapter ▶ · +15 · speed
//   FollowText   — the chapter's words, the spoken one lit, tap to seek
//   ChapterList  — every chapter, tap to jump, the current one marked
//
// ⚠ FollowText renders a chapter's words ONCE and moves the highlight by
//   touching two DOM nodes per word change. Re-rendering a few thousand
//   spans four times a second (every timeupdate) is how a phone drops
//   frames and the audio itself starts to stutter.
// ════════════════════════════════════════════════════════════════════

import React, { useEffect, useMemo, useRef, useState } from "react";
import { toggle, seek, setRate, seekChapter, nextChapter, prevChapter } from "./audio";
import { wordAt, cleanWord } from "./book";
import { fmtTime } from "./lyraeaData";

const C = {
  edge: "var(--edge)", gold: "var(--gold)", goldHi: "var(--gold-hi)", void: "var(--void)",
  bone: "var(--bone)", ash: "var(--ash)", smoke: "var(--smoke)", verd: "var(--verd)",
  display: "var(--display)", mono: "var(--mono)", sans: "var(--sans)",
};

// Scroll ONLY the list that holds `node`. scrollIntoView() scrolls every
// ancestor — including the fixed, overflow:hidden now-playing sheet, which
// can still be scrolled by script and then sits shoved off the screen.
function scrollWithin(node, where = "center", smooth = false) {
  let box = node?.parentElement;
  while (box && !/(auto|scroll)/.test(getComputedStyle(box).overflowY)) box = box.parentElement;
  if (!box) return;
  const b = box.getBoundingClientRect(), r = node.getBoundingClientRect();
  let delta;
  if (where === "band") {
    // Leave it alone while the word sits in the middle band of the view.
    const rel = (r.top - b.top) / b.height;
    if (rel >= 0.2 && rel <= 0.65) return;
    delta = r.top - b.top - (b.height - r.height) / 2;
  } else if (where === "nearest") {
    if (r.top < b.top) delta = r.top - b.top - 8;
    else if (r.bottom > b.bottom) delta = r.bottom - b.bottom + 8;
    else return;
  } else delta = r.top - b.top - (b.height - r.height) / 2;
  box.scrollTo({ top: box.scrollTop + delta, behavior: smooth ? "smooth" : "auto" });
}

const RATES = [0.75, 1, 1.25, 1.5, 1.75, 2, 2.5, 3];

const Btn = ({ children, onClick, title, size = 15 }) => (
  <button onClick={(e) => { e.stopPropagation(); onClick(); }} title={title} style={{
    background: "none", border: "none", color: C.bone, padding: 6,
    fontFamily: C.mono, fontSize: size, lineHeight: 1,
  }}>{children}</button>
);

export function BookControls({ a }) {
  const nextRate = () => setRate(RATES[(RATES.indexOf(a.rate) + 1) % RATES.length] || 1);
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 12, gap: 4 }}>
      <Btn onClick={() => seek(Math.max(0, a.position - 15))} title="Back 15 seconds" size={12}>↺15</Btn>
      <Btn onClick={prevChapter} title="Previous chapter" size={20}>⏮</Btn>
      <button onClick={toggle} style={{
        width: 62, height: 62, borderRadius: "50%",
        background: `linear-gradient(150deg, ${C.goldHi}, ${C.gold})`,
        border: "none", color: C.void, fontSize: 22, fontFamily: C.display,
        boxShadow: `0 8px 30px ${C.gold}55`,
      }}>{a.playing ? "❚❚" : "▶"}</button>
      <Btn onClick={nextChapter} title="Next chapter" size={20}>⏭</Btn>
      <Btn onClick={() => seek(a.position + 15)} title="Forward 15 seconds" size={12}>15↻</Btn>
      <button onClick={nextRate} title="Playback speed" style={{
        background: "none", border: `1px solid ${a.rate !== 1 ? C.gold : C.edge}`,
        color: a.rate !== 1 ? C.goldHi : C.smoke, fontFamily: C.mono, fontSize: 10,
        letterSpacing: ".08em", padding: "5px 7px", minWidth: 42,
      }}>{a.rate}×</button>
    </div>
  );
}

export function FollowText({ a, compact, mode, onMode }) {
  const ch = a.track.book.chapters[a.chapter];
  const box = useRef(null);
  const lit = useRef(null);
  const [follow, setFollow] = useState(() => {
    try { return localStorage.getItem("lyraea.follow") !== "0"; } catch { return true; }
  });

  const words = useMemo(() => (ch?.w || []).map((w, i) => {
    const shown = cleanWord(w);
    if (!shown) return null;
    return (
      <span key={i} data-i={i} className="ly-word"
        onClick={() => seek(ch.start + ch.t[i * 2] / 1000 + 0.01)}>{shown} </span>
    );
  }), [ch]);

  const idx = wordAt(ch, a.position);
  useEffect(() => {
    const root = box.current; if (!root) return;
    const node = idx >= 0 ? root.querySelector(`[data-i="${idx}"]`) : null;
    if (lit.current && lit.current !== node) lit.current.classList.remove("on");
    if (node) {
      node.classList.add("on");
      // Scroll only when the lit word leaves the middle band of the view.
      // Scrolling on EVERY word keeps the page in permanent motion and
      // makes the text impossible to read along with.
      if (follow && node !== lit.current) scrollWithin(node, "band", true);
    }
    lit.current = node;
  }, [idx, follow, words]);

  if (!ch?.w?.length) {
    return (
      <div style={{ padding: "40px 12px", textAlign: "center", fontFamily: C.sans, fontSize: 14, color: C.smoke, lineHeight: 1.55 }}>
        This file has chapters but no follow-along timing.<br />
        Bake it with <code>tools/lyraea_bake_book.py</code>, or drop its <code>.json</code> beside the MP3.
      </div>
    );
  }

  const chip = (on) => ({
    background: "none", border: `1px solid ${on ? C.gold : C.edge}`, color: on ? C.goldHi : C.smoke,
    fontFamily: C.mono, fontSize: 9, letterSpacing: ".16em", padding: "5px 8px", flex: "0 0 auto",
  });
  return (
    <div className={compact ? "ly-words-frame" : undefined} style={{ position: "relative" }}>
      <div className={compact ? "ly-words-head" : undefined} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 6, margin: "2px 2px 10px" }}>
        <div style={{
          fontFamily: compact ? C.mono : C.display, fontWeight: compact ? 400 : 800,
          fontSize: compact ? 10 : 19, letterSpacing: compact ? ".2em" : 0,
          color: compact ? C.gold : C.bone, textTransform: compact ? "uppercase" : "none",
          minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>{ch.title}</div>
        {onMode && (
          <button onClick={() => onMode(mode === "over" ? "side" : "over")} style={chip(mode === "over")}
            title="Side by side with the cover, or over a faded cover">
            {mode === "over" ? "OVERLAY" : "SIDE"}
          </button>
        )}
        <button onClick={() => { const v = !follow; setFollow(v); try { localStorage.setItem("lyraea.follow", v ? "1" : "0"); } catch {} }}
          style={chip(follow)}>FOLLOW {follow ? "ON" : "OFF"}</button>
      </div>
      {/* The words scroll (and fade at the edges) on their own; the header
          above stays put and fully visible. */}
      <div className={compact ? "ly-words-scroll ly-scroll" : undefined}>
        <div ref={box} className="ly-words" style={{ fontFamily: C.sans, fontSize: compact ? 21 : 19, lineHeight: 1.7,
          paddingTop: compact ? "10vh" : 0, paddingBottom: compact ? "22vh" : "40vh" }}>
          {words}
        </div>
      </div>
      {a.track.book.timing.includes("estimated") && (
        <div style={{ fontFamily: C.mono, fontSize: 9, color: C.smoke, letterSpacing: ".12em", marginTop: -30 }}>
          TIMING IS ESTIMATED BY READERS — CORRECT IT THERE, THEN RE-BAKE
        </div>
      )}
    </div>
  );
}

export function ChapterList({ a, onPicked, compact }) {
  const cs = a.track.book.chapters;
  const here = useRef(null);
  // Keep the current chapter in view as the book moves on — "nearest",
  // so a listener scrolling the list to browse is not yanked back while
  // the chapter they are hearing is still on screen.
  useEffect(() => { scrollWithin(here.current, "nearest"); }, [a.chapter]);
  useEffect(() => { scrollWithin(here.current, "center"); }, []);
  return (
    <div>
      {cs.map((c, i) => {
        const on = i === a.chapter;
        // Readers' part/chapter headings are real rows; indent the sections under them.
        const heading = /^(PART\b|Chapter\s+\d|THE\s)/.test(c.title) || c.title === c.title.toUpperCase();
        return (
          <div key={i} ref={on ? here : null} onClick={() => { seekChapter(i); onPicked?.(); }}
            className="ly-row" style={{
              display: "flex", alignItems: "center", gap: compact ? 8 : 10, padding: compact ? "7px 4px" : "10px 4px",
              borderBottom: `1px solid ${C.edge}`, cursor: "pointer",
              paddingLeft: heading ? 4 : 20,
            }}>
            <span style={{ fontFamily: C.mono, fontSize: 10, color: on ? C.goldHi : C.smoke, width: 26 }}>
              {on ? "◆" : String(i + 1).padStart(3, "0")}
            </span>
            <span style={{
              flex: 1, minWidth: 0, fontFamily: heading ? C.display : C.sans,
              fontWeight: heading ? 700 : 400, fontSize: compact ? 13 : 15, color: on ? C.goldHi : C.bone,
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>{c.title}</span>
            <span style={{ fontFamily: C.mono, fontSize: 10, color: C.smoke }}>{fmtTime(c.start)}</span>
          </div>
        );
      })}
    </div>
  );
}
